import { effect, Injectable, signal } from '@angular/core';

export type Theme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly STORAGE_KEY = 'hub_theme';
  private readonly DX_LIGHT_RE = /(^|\/)dx-light(\b|\.|-)/;
  private readonly DX_DARK_RE = /(^|\/)dx-dark(\b|\.|-)/;

  private _theme = signal<Theme>(this.resolveInitialTheme());
  readonly theme = this._theme.asReadonly();

  constructor() {
    effect(() => {
      const theme = this._theme();
      document.body.classList.remove('hub-theme-light', 'hub-theme-dark');
      document.body.classList.add(`hub-theme-${theme}`);
      localStorage.setItem(this.STORAGE_KEY, theme);
      this.applyDevExtremeTheme(theme);
    });
  }

  private applyDevExtremeTheme(theme: Theme): void {
    const links = document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]');
    links.forEach(link => {
      const href = link.href ?? '';
      if (this.DX_LIGHT_RE.test(href)) link.disabled = theme === 'dark';
      if (this.DX_DARK_RE.test(href)) link.disabled = theme !== 'dark';
    });
  }

  toggle(): void {
    this._theme.set(this._theme() === 'light' ? 'dark' : 'light');
  }

  setTheme(theme: Theme): void {
    this._theme.set(theme);
  }

  private resolveInitialTheme(): Theme {
    const stored = localStorage.getItem(this.STORAGE_KEY) as Theme | null;
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
}
